import classNames from 'classnames';
import { useMemo } from 'react';

import { ReplotChartNames } from '../../constants';
import { FormatType } from '../../types';
import { useChartTheme, useResizeObserver } from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { HorizontalStackedBar } from './horizontal-stacked-bar';
import styles from './stacked-bar-chart.module.css';
import {
  processStackedBarData,
  type StackedBarChartProps,
} from './stacked-bar-shared';
import { VerticalStackedBar } from './vertical-stacked-bar';

export type {
  StackedBarChartProps,
  StackedBarData,
  StackedBarOrientation,
} from './stacked-bar-shared';

export const StackedBarChart = ({
  data = [],
  loading = false,
  format = FormatType.INTEGER,
  colorOverride,
  colorPalette,
  hideLegend,
  maxLabelWidth = 75,
  formatLegendLabel = (label) => label,
  maxLegendLabelWidth,
  segmentOrder,
  normalized = true,
  orientation = 'horizontal',
  isTimeseries = false,
  highlightMatchingSegments = true,
}: StackedBarChartProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const { breakdownPalette } = useChartTheme();

  const isVertical = orientation === 'vertical';

  const model = useMemo(
    () =>
      processStackedBarData(data, {
        segmentOrder,
        normalized,
        colorOverride,
        colorPalette: colorPalette ?? breakdownPalette,
      }),
    [
      data,
      segmentOrder,
      normalized,
      colorOverride,
      colorPalette,
      breakdownPalette,
    ]
  );

  const Chart = isVertical ? VerticalStackedBar : HorizontalStackedBar;

  return (
    <div
      data-testid={plotTestId(
        isVertical
          ? ReplotChartNames.StackedBarChartVertical
          : ReplotChartNames.StackedBarChartHorizontal
      )}
      ref={containerRef}
      className={classNames(
        styles.container,
        isVertical && styles.containerVertical
      )}
    >
      <PlotLoadWrapper loading={loading} noData={model.noData}>
        <Chart
          model={model}
          width={width}
          height={height}
          format={format}
          hideLegend={hideLegend}
          maxLabelWidth={maxLabelWidth}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          isTimeseries={isTimeseries}
          highlightMatchingSegments={highlightMatchingSegments}
        />
      </PlotLoadWrapper>
    </div>
  );
};
